import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  Extension as ExtensionIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { UserSolicitation } from '../../types/solicitation';
import { analyticsService } from '../../services/analyticsService';

interface SolicitationCardProps {
  solicitation: UserSolicitation;
}

const getDeadlineInfo = (deadline: Date | undefined): { text: string; color: 'error' | 'warning' | 'success' | 'default' } => {
  if (!deadline) return { text: 'No deadline', color: 'default' };
  
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  
  if (days < 0) return { text: 'Expired', color: 'error' };
  if (days === 0) return { text: 'Due today', color: 'error' };
  if (days === 1) return { text: '1 day left', color: 'error' };
  if (days <= 3) return { text: `${days} days left`, color: 'warning' };
  if (days <= 7) return { text: `${days} days left`, color: 'success' };
  return { text: `${days} days left`, color: 'default' };
};

const getStatusColor = (status: string): 'primary' | 'success' | 'default' => {
  switch (status) {
    case 'active': return 'primary';
    case 'submitted': return 'success';
    default: return 'default';
  }
};

export const SolicitationCard: React.FC<SolicitationCardProps> = ({ solicitation }) => {
  const { metadata, proposalCount, emailCount } = solicitation;
  const deadlineInfo = getDeadlineInfo(metadata.deadline);

  const handleOpenSamGov = () => {
    analyticsService.openSamGov(solicitation.id, metadata.title);
    window.open(metadata.samGovUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenInExtension = () => {
    analyticsService.openExtension(solicitation.id, metadata.title);
    window.open(metadata.samGovUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 4,
        },
        transition: 'box-shadow 0.2s',
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Chip
            label={metadata.status.charAt(0).toUpperCase() + metadata.status.slice(1)}
            color={getStatusColor(metadata.status)}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={deadlineInfo.text}
            color={deadlineInfo.color}
            size="small"
            variant="outlined"
          />
        </Box>

        <Typography 
          variant="subtitle1" 
          component="h3" 
          sx={{ 
            fontWeight: 600, 
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
          }}
        >
          {metadata.title}
        </Typography>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2 }}
        >
          {metadata.department || metadata.agency}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {proposalCount} {proposalCount === 1 ? 'proposal' : 'proposals'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {emailCount} {emailCount === 1 ? 'email' : 'emails'}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenSamGov}
            sx={{ flex: 1 }}
          >
            SAM.gov
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<ExtensionIcon />}
            onClick={handleOpenInExtension}
            sx={{ flex: 1 }}
          >
            Extension
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
